import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  LinearProgress,
  useTheme,
  Typography,
  Divider,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { linearProgressClasses } from '@mui/material/LinearProgress';
import { tokens } from '../../../../global/theme/tokens';
import { ProjectUserStack } from '../ProjectUserStack';
import { renderHours } from '../../../../scenes/time-tracking/worktime';
import { useTranslation } from 'react-i18next';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  API_DELETE,
  API_PATCH,
  API_POST,
  DBRequest,
  useConfig,
} from '../../../../api/api';
import { SettingsPopover } from '../SettingsPopover';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { ProjectAddElementButton } from '../ProjectAddElementButton';
import { WPEForm } from '../work-package-elements/WPEForm';
import { WPE } from '../work-package-elements/WPE';
import { useRef, useState } from 'react';
import ExoDialog from '../../ExoDialog';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useTimeTracking } from '../../../time-tracking/AppTimeProvider';
import StopIcon from '@mui/icons-material/Stop';
import { EstimatedTime } from './EstimatedTime';
import { NavLink, useNavigate } from 'react-router-dom';
import Cookies from 'universal-cookie';
import { WPStatus } from '../../../special/Status';
import { LeadStatusSelector } from '../../../../scenes/leads/status/LeadStatusSelector';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DateLabel from '../../../label/DateLabel';
import moment from 'moment';
import { ExoAiOriginChip } from '../../ExoAiOriginChip';

const ProgressBar = styled(LinearProgress)(({ barcolor }) => ({
  height: 14,
  borderRadius: 7,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: 'rgba(128,128,128,0.2)',
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 4,
    backgroundColor: barcolor,
  },
}));

export const ProjectWorkPackage = ({
  project,
  workPackage,
  onChange,
  onDelete,
  editmode,
  onEdit,
  inGant,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();
  const navigate = useNavigate();

  const cookies = new Cookies();
  const userId = cookies.get('userId');

  const { clockIn, clockOut, activeProject, secondsIn } = useTimeTracking();

  const progressBarRef = useRef(null);
  const [openNewSE, setOpenNewSE] = useState(false);

  const {
    projectPosition,
    id,
    startDate,
    endDate,
    title,
    icon,
    status,
    isTimeTrackable,
    allowTimeTracking,
    users,
  } = workPackage || {};

  const settings = [
    {
      label: t('Edit'),
      Icon: EditIcon,
      onClick: () => onEdit(workPackage),
    },
    {
      label: t('Duplicate'),
      Icon: ContentCopyIcon,
      onClick: handleDuplicate,
    },
    {
      label: t('Delete'),
      Icon: DeleteIcon,
      onClick: handleDelete,
      color: 'error',
    },
  ];

  function handleDuplicate() {
    DBRequest({
      config,
      path: `work-packages/${id}/duplicate`,
      method: API_POST(),
      onResponse: (data) => onChange(data.data),
    });
  }

  function handleDelete() {
    DBRequest({
      config,
      path: `work-packages/${id}`,
      method: API_DELETE(),
      onResponse: () => onDelete(workPackage),
    });
  }

  function handleNewWPE(newElement) {
    const newElements = [...workPackage.elements, newElement];
    const updatedWorkPackage = { ...workPackage, elements: newElements };
    onChange(updatedWorkPackage);
  }

  function handleWPEDelete(elementToDel) {
    const newElements = workPackage.elements.filter(
      (obj) => obj.id !== elementToDel.id,
    );
    onChange({ ...workPackage, elements: newElements });
  }

  const activeWorkpackage =
    activeProject &&
    activeProject.activeWorkPackage &&
    activeProject.activeWorkPackage.id === id
      ? {
          ...activeProject.activeWorkPackage,
          totalTimeWorkedSeconds: secondsIn
            ? activeProject.activeWorkPackage.totalTimeWorkedSeconds + secondsIn
            : activeProject.activeWorkPackage.totalTimeWorkedSeconds,
        }
      : null;

  const workPercentage = activeWorkpackage
    ? activeWorkpackage.totalTimeWorkedSeconds / activeWorkpackage.estimatedTime
    : workPackage.totalTimeWorkedSeconds / workPackage.estimatedTime;

  function handleClockOut() {
    clockOut();
    onChange({
      ...workPackage,
      totalTimeWorkedSeconds: activeWorkpackage.totalTimeWorkedSeconds,
    });
  }

  function userHasAccess() {
    if (userId === project.supervisor.id) return true;
    const isInWorkPackage = workPackage.users.findIndex(
      (user) => user.id === userId,
    );
    return isInWorkPackage !== -1;
  }

  // status change
  const [anchorEl, setAnchorEl] = useState(null);
  const handleStatusClick = (event, item) => {
    event.preventDefault();
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };
  function handleStatusChange(status) {
    requestStatusChange(status.status);
  }

  function requestStatusChange(status) {
    DBRequest({
      config,
      path: `work-packages/${workPackage.id}`,
      method: API_PATCH({ status }),
      onResponse: () => {
        onChange({ ...workPackage, status });
      },
    });
  }

  const [editingProgress, setEditingProgress] = useState(false);
  const [confirmFinish, setConfirmFinish] = useState(false);

  // the progress of these work packages follows their finished tasks
  const canEditProgress = editmode && !workPackage.progressFromTasks;

  function handleProgressClick(e) {
    if (!canEditProgress) return;
    e.stopPropagation();
    if (!progressBarRef.current) return;
    const rect = progressBarRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const raw = Math.round((x / rect.width) * 100);
    const rounded = Math.round(raw / 5) * 5;
    const newProgress = Math.max(0, Math.min(100, rounded));
    updateProgress(newProgress);
  }

  function commitProgressInput(value) {
    const val = parseInt(value, 10);
    if (!isNaN(val)) {
      updateProgress(Math.max(0, Math.min(100, val)));
    }
    setEditingProgress(false);
  }

  function handleProgressInput(e) {
    if (e.key === 'Enter') {
      e.target.blur();
    } else if (e.key === 'Escape') {
      setEditingProgress(false);
    }
  }

  function handleProgressBlur(e) {
    commitProgressInput(e.target.value);
  }

  function updateProgress(newProgress) {
    onChange({ ...workPackage, progress: newProgress });
    DBRequest({
      config,
      path: `work-packages/${workPackage.id}`,
      method: API_PATCH({ progress: newProgress }),
    });
    if (newProgress >= 100 && workPackage.status !== 1) {
      setConfirmFinish(true);
    }
  }

  function handleConfirmFinish() {
    setConfirmFinish(false);
    DBRequest({
      config,
      path: `work-packages/${workPackage.id}`,
      method: API_PATCH({ status: 1 }),
      onResponse: () => {
        onChange({ ...workPackage, progress: 100, status: 1 });
      },
    });
  }

  const isAfterDeadline =
    workPackage.status === 0 &&
    moment(workPackage.endDate).isBefore(moment(), 'day');

  return (
    <div
      onClick={(e) => {
        // If clicking a non-nav element then STOP
        if (e.target.closest('.no-nav')) {
          e.stopPropagation();
          return;
        }

        // Otherwise navigate
        if (!userHasAccess() && !inGant) {
          navigate(
            `/projects/management/${project.id}/work-package/${workPackage.id}`,
          );
        }
      }}
      style={{ cursor: 'pointer' }}
    >
      <Box
        className={`group p-4 relative rounded-lg backdrop-blur-lg flex flex-col justify-between ${
          inGant ? 'min-w-[250px]' : ''
        }`}
        sx={{
          bgcolor: colors.glass,
          border: `2px solid ${isAfterDeadline ? colors.error : 'transparent'}`,
          '.add-wpe': {
            opacity: 0,
            transition: '0.5s ease',
            transform: 'scale(0)',
          },
          ':hover .add-wpe': {
            opacity: 1,
            transform: 'scale(1)',
          },
          '@media (max-width: 700px)': {
            '.add-wpe': {
              opacity: 1,
              transform: 'scale(1)',
            },
          },
        }}
      >
        {Boolean(startDate && endDate) && (
          <Typography
            className="absolute top-1 right-2 opacity-50"
            fontSize={10}
            fontWeight={600}
            sx={{
              color: isAfterDeadline ? colors.error : colors.text,
            }}
          >
            <DateLabel date={startDate} /> - <DateLabel date={endDate} />
          </Typography>
        )}
        <Box>
          <Box className="flex justify-between">
            <Typography
              fontSize="18px"
              fontWeight={500}
              className={`pb-2 flex items-center gap-4 ${
                inGant ? 'pr-5' : ''
              } overflow-hidden`}
              sx={{ maxWidth: '100%' }}
            >
              {icon && (
                <img
                  src={icon.temporaryUrl}
                  className="w-8 h-8 object-contain"
                />
              )}
              <ExoAiOriginChip record={workPackage} compact />
              {title}
              {inGant && (
                <NavLink
                  to={`/projects/management/${project.id}/work-package/${workPackage.id}`}
                >
                  <IconButton size="small">
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </NavLink>
              )}
            </Typography>
            {editmode && (
              <div className="no-nav opacity-0 add-wpe">
                <SettingsPopover settings={settings} />
              </div>
            )}
          </Box>
          <Box className="no-nav flex gap-2">
            <WPStatus
              number={status}
              onClick={(e) => {
                e.stopPropagation();
                handleStatusClick(e);
              }}
            />

            <LeadStatusSelector
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              onStatusChange={handleStatusChange}
              customStatuses={[
                {
                  label: t('Running'),
                  status: 0,
                  variant: t('running'),
                },
                {
                  label: t('Finished'),
                  status: 1,
                  variant: t('finished'),
                },
                {
                  label: t('Infinite'),
                  status: 2,
                  variant: t('infinite'),
                },
                {
                  label: t('Canceled'),
                  status: 3,
                  variant: t('rejected'),
                },
              ]}
            />
            {!inGant &&
              Boolean(isTimeTrackable) &&
              allowTimeTracking &&
              (activeWorkpackage ? (
                <Button
                  className="no-nav w-full text-xl"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClockOut();
                  }}
                  variant="outlined"
                  color="error"
                >
                  <StopIcon />
                  &nbsp;
                  {t('Clock Out')}
                </Button>
              ) : (
                <Button
                  className="no-nav w-full text-xl"
                  onClick={(e) => {
                    e.stopPropagation();
                    clockIn(project, workPackage);
                  }}
                  variant="outlined"
                  color="success"
                  size="small"
                >
                  <PlayArrowIcon />
                  &nbsp;
                  {t('Clock In')}
                </Button>
              ))}
          </Box>
          {status !== 2 && <Box className="no-nav flex items-center gap-1 mt-2">
            <Typography
              variant="caption"
              sx={{ flexShrink: 0, opacity: 0.6, width: 90 }}
            >
              {t('Work Progress')}
            </Typography>
            <Tooltip
              title={
                workPackage.progressFromTasks
                  ? `${workPackage.progress || 0}% – ${t('Calculated from the finished tasks')}`
                  : canEditProgress
                    ? `${workPackage.progress || 0}% – ${t('Click to change')}`
                    : `${workPackage.progress || 0}%`
              }
            >
              <Box
                ref={progressBarRef}
                onClick={handleProgressClick}
                sx={{
                  flex: 1,
                  minWidth: 60,
                  cursor: canEditProgress ? 'pointer' : 'default',
                  '&:hover': canEditProgress ? { opacity: 0.8 } : {},
                }}
              >
                <ProgressBar
                  variant="determinate"
                  value={workPackage.progress || 0}
                  barcolor={colors.success}
                />
              </Box>
            </Tooltip>
            {canEditProgress && editingProgress ? (
              <input
                autoFocus
                type="number"
                min={0}
                max={100}
                defaultValue={workPackage.progress || 0}
                onKeyDown={handleProgressInput}
                onBlur={handleProgressBlur}
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: 40,
                  textAlign: 'right',
                  fontSize: '0.75rem',
                  fontFamily: 'monospace',
                  border: 'none',
                  borderBottom: `2px solid ${colors.success}`,
                  background: 'transparent',
                  color: 'inherit',
                  outline: 'none',
                }}
              />
            ) : (
              <Typography
                variant="caption"
                className="monospace"
                onClick={(e) => {
                  if (!canEditProgress) return;
                  e.stopPropagation();
                  setEditingProgress(true);
                }}
                sx={{
                  flexShrink: 0,
                  width: 35,
                  textAlign: 'right',
                  cursor: canEditProgress ? 'pointer' : 'default',
                  '&:hover': canEditProgress
                    ? { textDecoration: 'underline' }
                    : {},
                }}
              >
                {workPackage.progress || 0}%
              </Typography>
            )}
          </Box>}
          <Box className="pb-4">
            <Box className="flex flex-col gap-1">
              {workPackage.estimatedTime && (
                <Box className="flex items-center gap-1 mt-1">
                  <Typography
                    variant="caption"
                    sx={{ flexShrink: 0, opacity: 0.6, width: 90 }}
                  >
                    {t('Worked')}
                  </Typography>
                  <Tooltip
                    title={`${renderHours(
                      (activeWorkpackage
                        ? activeWorkpackage.totalTimeWorkedSeconds
                        : workPackage.totalTimeWorkedSeconds) / 60,
                    )}h / ${renderHours(workPackage.estimatedTime / 3600)}h`}
                  >
                    <Box sx={{ flex: 1, minWidth: 60 }}>
                      <ProgressBar
                        variant="determinate"
                        value={Math.min(workPercentage * 100, 100)}
                        barcolor={
                          workPercentage <= 1
                            ? colors.info
                            : colors.redAccent[500]
                        }
                      />
                    </Box>
                  </Tooltip>
                  <Typography
                    variant="caption"
                    className="monospace"
                    sx={{
                      flexShrink: 0,
                      width: 35,
                      textAlign: 'right',
                      color:
                        workPercentage > 1
                          ? colors.redAccent[500]
                          : undefined,
                    }}
                  >
                    {Math.round(workPercentage * 100)}%
                  </Typography>
                </Box>
              )}

              <Box className="flex flex-col gap-1 pt-2">
                {workPackage?.elements?.length > 0 &&
                  workPackage.elements.map((element) => (
                    <WPE
                      key={element.id}
                      element={element}
                      mode="preview"
                      editmode={editmode}
                      onDelete={handleWPEDelete}
                    />
                  ))}
              </Box>
            </Box>
            {editmode && !inGant && (
              <>
                <ExoDialog
                  limitWidth
                  open={openNewSE}
                  className="no-nav overflow-y-auto"
                >
                  <WPEForm
                    workPackage={workPackage}
                    onCreate={handleNewWPE}
                    onClose={() => setOpenNewSE(false)}
                  />
                </ExoDialog>
                <Tooltip title={t('Add Work Package Element')}>
                  <Box className="add-wpe">
                    <ProjectAddElementButton
                      sizePx="15px"
                      onClick={() => setOpenNewSE(true)}
                    />
                  </Box>
                </Tooltip>
              </>
            )}
          </Box>
        </Box>

        <Box className="flex flex-col">
          <Divider />
          <Box className="flex justify-between items-start">
            <EstimatedTime workPackage={workPackage} />

            <ProjectUserStack
              editmode={editmode}
              users={users}
              workPackage={workPackage}
              onChange={onChange}
              projectId={project.id}
            />
          </Box>
        </Box>
      </Box>
      <Dialog
        open={confirmFinish}
        onClose={() => setConfirmFinish(false)}
        onClick={(e) => e.stopPropagation()}
      >
        <DialogContent>
          <DialogContentText>
            {t('Mark work package as finished?')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmFinish(false)}>{t('No')}</Button>
          <Button onClick={handleConfirmFinish} variant="contained" color="success" autoFocus>
            {t('Yes')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
